// Pointer navigation (mouse + touch), section-arrow dragging and throttled hover.
import * as THREE from 'three';
import { clamp } from './math';
import {
  dollyLocal,
  dollyTowardPivot,
  orbitLocal,
  panLocal
} from './camera-rig';
import { isWampAnimating, retainViewDistanceAfterUserZoom } from './wamp';
import { clearHoverHighlight, handleMeasureClick, isMeasureMode, pickFaceOnModel, updateHoverAt } from './measure';
import { selectTreeObject } from './model-tree';
import {
  getSectionArrow,
  getSectionArrowCenter,
  getSectionPlane,
  handleSectionPick,
  isSectionActive,
  isSectionPickMode,
  pickOriginPlaneGuide,
  rebuildSectionPlane,
  setOriginPlaneHover,
  setSectionOffset,
  startOriginSection
} from './section';
import { dom, formatOffset } from './ui';
import { camera, getModelRadius, raycaster, requestRender, viewHeightAtPivot } from './viewer';

const CLICK_MOVE_THRESHOLD = 6;
/** Hover picking raycasts the whole model, so it is coalesced to ~30 Hz. */
const HOVER_INTERVAL_MS = 32;

/** World units per screen pixel at the orbit pivot (1:1 grab feel). */
function panWorldPerPixel(): number {
  const height = Math.max(dom.canvas.clientHeight, 1);
  return viewHeightAtPivot() / height;
}

type TrackedPointer = {
  id: number;
  x: number;
  y: number;
  button: number;
  pointerType: string;
};

const pointers = new Map<number, TrackedPointer>();

const dragState = {
  down: false,
  button: 0,
  lastX: 0,
  lastY: 0,
  downX: 0,
  downY: 0,
  /** True once two or more pointers were active — suppresses tap/click. */
  multi: false
};

let sectionDragging = false;
let sectionDragStartOffset = 0;
let sectionDragStartX = 0;
let sectionDragStartY = 0;
let sectionDragScreenDir = { x: 0, y: -1 };
let sectionDragWorldPerPixel = 0;

let pinchLastDist = 0;
let pinchLastMidX = 0;
let pinchLastMidY = 0;

function pickSectionArrow(clientX: number, clientY: number): boolean {
  const arrow = getSectionArrow();
  if (!arrow) return false;
  const rect = dom.canvas.getBoundingClientRect();
  const ndc = new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
  raycaster.setFromCamera(ndc, camera);
  return raycaster.intersectObject(arrow, true).length > 0;
}

function beginSectionDrag(clientX: number, clientY: number): void {
  sectionDragging = true;
  sectionDragStartOffset = parseFloat(dom.sectionOffsetSlider.value) || 0;
  sectionDragStartX = clientX;
  sectionDragStartY = clientY;
  dom.canvas.classList.add('dragging');

  // World-space distance per screen pixel along the plane's normal, measured
  // once at drag start (valid for the whole drag since the camera doesn't move
  // while dragging the plane).
  const rect = dom.canvas.getBoundingClientRect();
  const center = getSectionArrowCenter();
  const testDist = Math.max(getModelRadius() * 0.05, 0.01);
  const p1 = center.clone();
  const p2 = center.clone().addScaledVector(getSectionPlane().normal, testDist);
  const s1 = p1.clone().project(camera);
  const s2 = p2.clone().project(camera);
  const sx1 = (s1.x * 0.5 + 0.5) * rect.width, sy1 = (-s1.y * 0.5 + 0.5) * rect.height;
  const sx2 = (s2.x * 0.5 + 0.5) * rect.width, sy2 = (-s2.y * 0.5 + 0.5) * rect.height;
  const screenDx = sx2 - sx1, screenDy = sy2 - sy1;
  const screenDist = Math.hypot(screenDx, screenDy) || 1;
  sectionDragScreenDir = { x: screenDx / screenDist, y: screenDy / screenDist };
  sectionDragWorldPerPixel = testDist / screenDist;
}

function updateSectionDrag(clientX: number, clientY: number): void {
  const dx = clientX - sectionDragStartX;
  const dy = clientY - sectionDragStartY;
  const alongScreen = dx * sectionDragScreenDir.x + dy * sectionDragScreenDir.y;
  let newOffset = sectionDragStartOffset + alongScreen * sectionDragWorldPerPixel;
  newOffset = clamp(newOffset, parseFloat(dom.sectionOffsetSlider.min), parseFloat(dom.sectionOffsetSlider.max));
  setSectionOffset(newOffset);
}

function pointerList(): TrackedPointer[] {
  return [...pointers.values()];
}

function syncPinchBaseline(): void {
  const pts = pointerList();
  if (pts.length < 2) {
    pinchLastDist = 0;
    return;
  }
  const [a, b] = pts;
  pinchLastDist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  pinchLastMidX = (a.x + b.x) / 2;
  pinchLastMidY = (a.y + b.y) / 2;
}

function applyPinchPanZoom(): void {
  const pts = pointerList();
  if (pts.length < 2) return;
  const [a, b] = pts;
  const dist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2;
  if (pinchLastDist > 0) {
    const zoomRatio = dist / pinchLastDist;
    const factor = clamp((zoomRatio - 1) * 0.9, -0.35, 0.35);
    if (Math.abs(factor) > 1e-4) dollyLocal(factor);

    const mdx = midX - pinchLastMidX;
    const mdy = midY - pinchLastMidY;
    if (mdx || mdy) {
      const scale = panWorldPerPixel();
      panLocal(mdx * scale, mdy * scale);
    }
  }
  pinchLastDist = dist;
  pinchLastMidX = midX;
  pinchLastMidY = midY;
}

function endSectionDrag(): void {
  sectionDragging = false;
  if (pointers.size === 0) dom.canvas.classList.remove('dragging');
}

function handleTap(clientX: number, clientY: number): void {
  if (isMeasureMode()) {
    handleMeasureClick(clientX, clientY);
    return;
  }
  const originId = pickOriginPlaneGuide(clientX, clientY);
  if (originId) {
    startOriginSection(originId);
    return;
  }
  if (isSectionPickMode()) {
    handleSectionPick(clientX, clientY);
    return;
  }
  // Plain click: select the part under the cursor in the tree (empty space clears).
  const hit = pickFaceOnModel(clientX, clientY);
  selectTreeObject(hit ? hit.mesh : null);
}

// ---------------------------------------------------------------------------
// Throttled hover work (face highlight + arrow cursor), coalesced into one rAF
// ---------------------------------------------------------------------------

let hoverScheduled = false;
let hoverX = 0;
let hoverY = 0;
let lastHoverRun = 0;

function scheduleHover(clientX: number, clientY: number): void {
  hoverX = clientX;
  hoverY = clientY;
  if (hoverScheduled) return;
  hoverScheduled = true;
  requestAnimationFrame(runHover);
}

function runHover(now: number): void {
  hoverScheduled = false;
  if (now - lastHoverRun < HOVER_INTERVAL_MS) {
    hoverScheduled = true;
    requestAnimationFrame(runHover);
    return;
  }
  lastHoverRun = now;

  if (isMeasureMode()) {
    if (dragState.down || sectionDragging || pointers.size > 1) { clearHoverHighlight(); return; }
    updateHoverAt(hoverX, hoverY);
    return;
  }

  clearHoverHighlight();
  if (sectionDragging || dragState.down || pointers.size > 1) {
    setOriginPlaneHover(null);
    return;
  }
  // Clickable RGB origin-plane guides (section panel open, before a cut is active).
  const originHover = pickOriginPlaneGuide(hoverX, hoverY);
  setOriginPlaneHover(originHover);
  if (originHover) {
    dom.canvas.style.cursor = 'pointer';
    return;
  }
  if (isSectionPickMode()) return;
  // Cursor feedback: distinct cursor when hovering the section drag arrow.
  if (!isSectionActive()) {
    if (!isMeasureMode()) dom.canvas.style.cursor = 'grab';
    return;
  }
  dom.canvas.style.cursor = pickSectionArrow(hoverX, hoverY) ? 'pointer' : 'grab';
}

// ---------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------

export function initControls(): void {
  dom.canvas.addEventListener('contextmenu', e => e.preventDefault());

  dom.canvas.addEventListener('pointerdown', e => {
    if (e.pointerType === 'mouse' && e.button > 2) return;

    pointers.set(e.pointerId, {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      button: e.button,
      pointerType: e.pointerType
    });

    try {
      dom.canvas.setPointerCapture(e.pointerId);
    } catch {
      /* ignore — some browsers reject capture in edge cases */
    }

    if (pointers.size >= 2) {
      dragState.multi = true;
      dragState.down = false;
      if (sectionDragging) endSectionDrag();
      syncPinchBaseline();
      dom.canvas.classList.add('dragging');
      clearHoverHighlight();
      setOriginPlaneHover(null);
      return;
    }

    const primary = e.pointerType !== 'mouse' || e.button === 0;
    if (
      primary &&
      isSectionActive() &&
      !isSectionPickMode() &&
      !isMeasureMode() &&
      pickSectionArrow(e.clientX, e.clientY)
    ) {
      beginSectionDrag(e.clientX, e.clientY);
      return;
    }

    dragState.down = true;
    dragState.button = e.pointerType === 'touch' || e.pointerType === 'pen' ? 0 : e.button;
    dragState.lastX = e.clientX;
    dragState.lastY = e.clientY;
    dragState.downX = e.clientX;
    dragState.downY = e.clientY;
    dom.canvas.classList.add('dragging');
  });

  window.addEventListener('pointermove', e => {
    const tracked = pointers.get(e.pointerId);
    if (tracked) {
      tracked.x = e.clientX;
      tracked.y = e.clientY;
    }

    if (sectionDragging && pointers.size <= 1) {
      updateSectionDrag(e.clientX, e.clientY);
      return;
    }

    if (pointers.size >= 2) {
      applyPinchPanZoom();
      return;
    }

    if (dragState.down) {
      const dx = e.clientX - dragState.lastX;
      const dy = e.clientY - dragState.lastY;
      dragState.lastX = e.clientX;
      dragState.lastY = e.clientY;
      // Inventor-style: LMB / one-finger orbit; MMB pan; Shift+MMB orbit; RMB pan.
      const orbit = dragState.button === 0 || (dragState.button === 1 && e.shiftKey);
      if (orbit) {
        orbitLocal(-dy * 0.006, -dx * 0.006, 0);
      } else {
        const scale = panWorldPerPixel();
        panLocal(dx * scale, dy * scale);
      }
      return;
    }

    if (e.pointerType !== 'touch') scheduleHover(e.clientX, e.clientY);
  });

  const onPointerUp = (e: PointerEvent): void => {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);

    try {
      if (dom.canvas.hasPointerCapture(e.pointerId)) {
        dom.canvas.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* ignore */
    }

    if (sectionDragging && pointers.size === 0) {
      endSectionDrag();
      dragState.multi = false;
      return;
    }

    if (pointers.size >= 2) {
      syncPinchBaseline();
      return;
    }

    if (pointers.size === 1) {
      // Dropped from pinch back to one finger: resume orbit without a tap.
      const rem = pointerList()[0]!;
      dragState.down = true;
      dragState.button = 0;
      dragState.lastX = rem.x;
      dragState.lastY = rem.y;
      pinchLastDist = 0;
      return;
    }

    // All pointers up.
    const wasDragging = dragState.down;
    const button = dragState.button;
    const downX = dragState.downX;
    const downY = dragState.downY;
    const suppressClick = dragState.multi;
    dragState.down = false;
    dragState.multi = false;
    pinchLastDist = 0;
    dom.canvas.classList.remove('dragging');

    if (suppressClick || !wasDragging) return;
    const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
    if (button !== 0 || moved >= CLICK_MOVE_THRESHOLD) return;
    handleTap(e.clientX, e.clientY);
  };

  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);

  dom.canvas.addEventListener('wheel', e => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 0.15 : -0.15;
    // CAD-style: SpaceMouse can keep rotating while the wheel zooms.
    // During cap motion the driver overwrites view.affine each frame, so we
    // retain the user distance across those writes.
    if (isWampAnimating()) {
      dollyTowardPivot(factor);
      retainViewDistanceAfterUserZoom();
    } else {
      dollyLocal(factor);
    }
  }, { passive: false });

  dom.canvas.addEventListener('pointerleave', e => {
    if (e.pointerType === 'touch') return;
    if (dragState.down || sectionDragging || pointers.size > 0) return;
    clearHoverHighlight();
    setOriginPlaneHover(null);
  });

  dom.sectionOffsetSlider.addEventListener('input', () => {
    dom.sectionOffsetValue.textContent = formatOffset(parseFloat(dom.sectionOffsetSlider.value));
    rebuildSectionPlane();
    requestRender();
  });
}
